import type { ExtractionResult } from "../lib/api";

interface Props {
  result: ExtractionResult;
}

const PALETTE = [
  "#ff5b35",
  "#1f77b4",
  "#2ca02c",
  "#9467bd",
  "#e377c2",
  "#bcbd22",
  "#17becf",
  "#8c564b",
];

const WIDTH = 640;
const HEIGHT = 420;
const MARGIN = { top: 28, right: 120, bottom: 56, left: 72 };
const PLOT_W = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_H = HEIGHT - MARGIN.top - MARGIN.bottom;

/**
 * Renders the extracted data as a clean SVG chart, intended to be displayed
 * side-by-side with the original source image. Eli verifies extraction by
 * eyeballing the two charts — if they look the same, extraction is correct;
 * if they diverge, something's wrong.
 *
 * Trust signal: this re-render uses only the extracted data values. No pixel-
 * mapping back onto the source image. Render accuracy depends only on
 * extraction accuracy (no compounded anchor-position error).
 */
export function ChartReproduction({ result }: Props) {
  const isBar = result.chart_type === "bar";
  const isStackedBar =
    result.chart_type === "stacked_bar" ||
    result.chart_type === "stacked-bar" ||
    result.chart_type === "stacked bar";
  const isScatter = result.chart_type === "scatter";
  const isArea =
    result.chart_type === "area" || result.chart_type === "stacked_area";

  const isCategorical =
    result.x_axis.scale === "category" ||
    result.series[0]?.points.every((p) => typeof p.x === "string") ||
    false;

  const xValues: (number | string)[] = isCategorical
    ? Array.from(
        new Set(
          result.series.flatMap((s) => s.points.map((p) => String(p.x)))
        )
      )
    : (() => {
        const all = result.series
          .flatMap((s) => s.points.map((p) => Number(p.x)))
          .filter(Number.isFinite);
        if (!all.length) return [0, 1];
        return [Math.min(...all), Math.max(...all)];
      })();

  // For stacked bars, y-axis must accommodate stacked totals, not per-series max
  const stackedTotalsByX = new Map<string, number>();
  if (isStackedBar) {
    result.series.forEach((s) => {
      s.points.forEach((p) => {
        const key = String(p.x);
        stackedTotalsByX.set(key, (stackedTotalsByX.get(key) ?? 0) + (Number.isFinite(p.y) ? p.y : 0));
      });
    });
  }
  const allY = isStackedBar
    ? Array.from(stackedTotalsByX.values())
    : result.series.flatMap((s) => s.points.map((p) => p.y)).filter(Number.isFinite);
  let yMin = Math.min(0, ...allY);
  let yMax = Math.max(1, ...allY);
  if (yMin === yMax) yMax = yMin + 1;
  const yPad = (yMax - yMin) * 0.08;
  yMax = yMax + yPad;
  // Don't pad below 0 for typical positive-only data
  if (yMin < 0) yMin = yMin - yPad;

  const xToPx = (xv: number | string): number => {
    if (isCategorical) {
      const idx = xValues.indexOf(typeof xv === "string" ? xv : String(xv));
      if (idx < 0) return -9999;
      const step = PLOT_W / xValues.length;
      return MARGIN.left + step * (idx + 0.5);
    }
    const xn = Number(xv);
    const x0 = Number(xValues[0]);
    const x1 = Number(xValues[xValues.length - 1]);
    if (x1 === x0) return MARGIN.left + PLOT_W / 2;
    return MARGIN.left + ((xn - x0) / (x1 - x0)) * PLOT_W;
  };

  const yToPx = (y: number): number => {
    return MARGIN.top + PLOT_H - ((y - yMin) / (yMax - yMin)) * PLOT_H;
  };

  const formatNum = (n: number): string => {
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
    if (Math.abs(n) >= 1) return Number.isInteger(n) ? String(n) : n.toFixed(1);
    return n.toFixed(2);
  };

  const yTickCount = 6;
  const yTicks: number[] = [];
  for (let i = 0; i <= yTickCount; i++) {
    yTicks.push(yMin + ((yMax - yMin) * i) / yTickCount);
  }

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="w-full block rounded-md border border-ink/10 bg-paper"
      role="img"
      aria-label="Extracted data re-rendered as a chart"
    >
      {/* y gridlines + ticks */}
      {yTicks.map((y, i) => (
        <g key={`y-${i}`}>
          <line
            x1={MARGIN.left}
            x2={WIDTH - MARGIN.right}
            y1={yToPx(y)}
            y2={yToPx(y)}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
          <text
            x={MARGIN.left - 8}
            y={yToPx(y)}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize="11"
            fill="#6b7280"
          >
            {formatNum(y)}
          </text>
        </g>
      ))}

      {/* x labels */}
      {isCategorical
        ? xValues.map((xv, i) => (
            <text
              key={`x-${i}`}
              x={MARGIN.left + (PLOT_W / xValues.length) * (i + 0.5)}
              y={HEIGHT - MARGIN.bottom + 18}
              textAnchor="middle"
              fontSize="11"
              fill="#6b7280"
            >
              {String(xv)}
            </text>
          ))
        : [Number(xValues[0]), Number(xValues[xValues.length - 1])].map(
            (xv, i, arr) => (
              <text
                key={`x-${i}`}
                x={MARGIN.left + (i / (arr.length - 1)) * PLOT_W}
                y={HEIGHT - MARGIN.bottom + 18}
                textAnchor={i === 0 ? "start" : "end"}
                fontSize="11"
                fill="#6b7280"
              >
                {formatNum(xv)}
              </text>
            )
          )}

      {/* series */}
      {(() => {
        // Stacked bars need cross-series state (running cumulative per x).
        // Pre-compute so series rendering stays straightforward.
        const stackedRunningByX = new Map<string, number>();
        return result.series.map((s, sIdx) => {
          const colour = PALETTE[sIdx % PALETTE.length];

          if (isStackedBar) {
            const slotWidth = PLOT_W / Math.max(1, xValues.length);
            const barWidth = slotWidth * 0.72;
            return (
              <g key={`s-${sIdx}`}>
                {s.points.map((p, j) => {
                  const xKey = String(p.x);
                  const cx = xToPx(p.x);
                  if (cx < 0) return null;
                  const prev = stackedRunningByX.get(xKey) ?? 0;
                  const yVal = Number.isFinite(p.y) ? p.y : 0;
                  const next = prev + yVal;
                  stackedRunningByX.set(xKey, next);
                  const yTop = yToPx(next);
                  const yBottom = yToPx(prev);
                  return (
                    <rect
                      key={j}
                      x={cx - barWidth / 2}
                      y={yTop}
                      width={barWidth}
                      height={Math.max(0, yBottom - yTop)}
                      fill={colour}
                    />
                  );
                })}
              </g>
            );
          }

          if (isBar) {
          const seriesCount = result.series.length;
          const slotWidth = PLOT_W / xValues.length;
          const barWidth = (slotWidth * 0.78) / seriesCount;
          const groupStart = -(slotWidth * 0.39);
          return (
            <g key={`s-${sIdx}`}>
              {s.points.map((p, j) => {
                const cx = xToPx(p.x);
                if (cx < 0) return null;
                const py = yToPx(p.y);
                const y0 = yToPx(0);
                const barX = cx + groupStart + barWidth * sIdx;
                const barY = Math.min(py, y0);
                const barH = Math.max(0, Math.abs(y0 - py));
                return (
                  <rect
                    key={j}
                    x={barX}
                    y={barY}
                    width={barWidth}
                    height={barH}
                    fill={colour}
                  />
                );
              })}
            </g>
          );
        }

        if (isScatter) {
          return (
            <g key={`s-${sIdx}`}>
              {s.points.map((p, j) => {
                const cx = xToPx(p.x);
                if (cx < 0) return null;
                return (
                  <circle
                    key={j}
                    cx={cx}
                    cy={yToPx(p.y)}
                    r="3.5"
                    fill={colour}
                    opacity="0.85"
                  />
                );
              })}
            </g>
          );
        }

          // line (default for line / area / others)
          const pts = s.points
            .map((p) => ({ x: xToPx(p.x), y: yToPx(p.y) }))
            .filter((pt) => pt.x >= 0);
          const path = pts
            .map((pt, j) => `${j === 0 ? "M" : "L"} ${pt.x} ${pt.y}`)
            .join(" ");
          if (isArea && pts.length > 1) {
            // area fill from line down to baseline
            const baselineY = yToPx(0);
            const areaPath =
              path +
              ` L ${pts[pts.length - 1].x} ${baselineY}` +
              ` L ${pts[0].x} ${baselineY} Z`;
            return (
              <g key={`s-${sIdx}`}>
                <path d={areaPath} fill={colour} fillOpacity="0.3" />
                <path d={path} stroke={colour} strokeWidth="2" fill="none" />
              </g>
            );
          }
          return (
            <g key={`s-${sIdx}`}>
              <path d={path} stroke={colour} strokeWidth="2" fill="none" />
              {pts.map((pt, j) => (
                <circle key={j} cx={pt.x} cy={pt.y} r="2.5" fill={colour} />
              ))}
            </g>
          );
        });
      })()}

      {/* axes */}
      <line
        x1={MARGIN.left}
        x2={WIDTH - MARGIN.right}
        y1={HEIGHT - MARGIN.bottom}
        y2={HEIGHT - MARGIN.bottom}
        stroke="#374151"
        strokeWidth="1"
      />
      <line
        x1={MARGIN.left}
        x2={MARGIN.left}
        y1={MARGIN.top}
        y2={HEIGHT - MARGIN.bottom}
        stroke="#374151"
        strokeWidth="1"
      />

      {/* axis labels */}
      {result.y_axis.label && (
        <text
          x={20}
          y={MARGIN.top + PLOT_H / 2}
          textAnchor="middle"
          fontSize="11"
          fill="#374151"
          transform={`rotate(-90, 20, ${MARGIN.top + PLOT_H / 2})`}
        >
          {result.y_axis.label}
          {result.y_axis.units ? ` (${result.y_axis.units})` : ""}
        </text>
      )}
      {result.x_axis.label && (
        <text
          x={MARGIN.left + PLOT_W / 2}
          y={HEIGHT - 10}
          textAnchor="middle"
          fontSize="11"
          fill="#374151"
        >
          {result.x_axis.label}
          {result.x_axis.units ? ` (${result.x_axis.units})` : ""}
        </text>
      )}

      {/* title (if present) */}
      {result.title && (
        <text
          x={MARGIN.left + PLOT_W / 2}
          y={MARGIN.top - 10}
          textAnchor="middle"
          fontSize="12"
          fontWeight="600"
          fill="#111827"
        >
          {result.title}
        </text>
      )}

      {/* legend */}
      <g transform={`translate(${WIDTH - MARGIN.right + 12}, ${MARGIN.top})`}>
        {result.series.map((s, i) => (
          <g key={s.label} transform={`translate(0, ${i * 18})`}>
            <rect width="12" height="12" fill={PALETTE[i % PALETTE.length]} />
            <text x={18} y={10} fontSize="11" fill="#374151">
              {s.label}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}
